import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGetProjects, useGetClaims } from '@edison/api-client-react';
import { customInstance } from '@edison/api-client-react';
import { ShieldCheck, ChevronRight, AlertCircle, CheckCircle2, Clock, ChevronDown, FileText, Package, RefreshCw, Info, Search } from 'lucide-react';
import { Link } from 'wouter';
import { useAuthStore } from '@/store/auth-store.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { Button } from '@/components/ui/button.js';

interface TfaPanelistRef {
  userId: string;
  name: string | null;
  hasCompetenceProfile: boolean;
  profilePinned: boolean;
}

interface TfaSection {
  stage: string;
  locked: boolean;
  problemStatement: string | null;
  cotsSweepNotes: string | null;
  aiDraftCotsSweepItems: Array<{ name: string; whyConsidered: string; whyRejected: string }> | null;
  determination: { determination: string; notes: string | null } | null;
  deliberationCriteria: Array<{ questionKey: string; panelPositive: number; panelNegative: number; comments: string[] }>;
  // Optional: absent from reports cached before the rubric-v2 deploy.
  panelists?: TfaPanelistRef[];
}

interface CoreActivityReport {
  id: string;
  name: string;
  description?: string | null;
  ipOwnership?: string | null;
  competenceRecords?: Array<{ confirmedAt: string | null }>;
}

interface DocumentationAppendix {
  projectPlansAndPrds: unknown[];
  githubArtefacts: unknown[];
  testingAndBenchmarking: unknown[];
  // Optional: absent from reports cached before the rubric-v2 deploy.
  experimentation?: unknown[];
  aiModelEvaluation: unknown[];
  userFeedback: unknown[];
  other: unknown[];
}

export interface ProjectEligibilityReport {
  projectId: string;
  projectName: string;
  incomeYear: string | null;
  tfa: TfaSection | null;
  coreActivities: CoreActivityReport[];
  documentationAppendix: DocumentationAppendix;
  generatedAt: string;
}

type AuditStatus = 'audit_ready' | 'gaps' | 'not_started';

interface GapItem {
  label: string;
  href: string;
}

export function deriveStatus(report: ProjectEligibilityReport, pendingCount: number): AuditStatus {
  if (!report.tfa?.locked) return 'not_started';
  const gaps = deriveGaps(report, pendingCount);
  if (gaps.length === 0) return 'audit_ready';
  return 'gaps';
}

// Rubric v2 — deterministic auditor-facing checks against the current data model.
// Independent of the defensibility scorer by design (that is the internal AI gauge; this is
// "what would an auditor ask for"). See docs/superpowers/specs/
// 2026-07-25-audit-readiness-defensibility-rebuild-design.md for each rule's rationale.
export function deriveGaps(report: ProjectEligibilityReport, pendingCount: number): GapItem[] {
  const gaps: GapItem[] = [];
  const projectHref = `/projects/${report.projectId}`;

  if (!report.tfa?.locked) {
    gaps.push({ label: 'TFA not locked', href: projectHref });
  }

  // Locked panel must have a formal determination recorded.
  if (report.tfa?.locked && !report.tfa?.determination) {
    gaps.push({ label: 'TFA determination missing', href: projectHref });
  }

  // Competent-professional evidence (leads-only model): every panelist needs a competence
  // profile — pinned on the panelist row or a current profile. Absence of per-activity
  // competence records is deliberately NOT a gap.
  for (const p of report.tfa?.panelists ?? []) {
    if (!p.hasCompetenceProfile) {
      gaps.push({ label: `Panelist competence profile missing — ${p.name ?? p.userId}`, href: projectHref });
    }
  }

  if (report.coreActivities.length === 0) {
    gaps.push({ label: 'No core R&D activities defined', href: projectHref });
  }

  for (const act of report.coreActivities) {
    if (act.ipOwnership === 'undetermined') {
      gaps.push({ label: `IP ownership undetermined — ${act.name}`, href: `/activities/${act.id}` });
    }
    if (act.ipOwnership === 'client') {
      gaps.push({ label: `High risk: client IP — ${act.name}`, href: `/activities/${act.id}` });
    }
    // Per-activity competence records are not checked here. Competence is established at the
    // project level: the lead (TFA panelist) holds a profile that covers all project activities.
    // The panelist profile gap above is the gate; per-activity records are the old per-developer model.
  }

  if (report.documentationAppendix.githubArtefacts.length === 0) {
    gaps.push({ label: 'No engineering evidence (GitHub)', href: projectHref });
  }

  // Attempt trails (abandoned_work) are systematic-progression evidence — either they or
  // formal testing/benchmarking artefacts or GitHub commit history satisfy this.
  // GitHub commits contain iterative engineering evidence (CI runs, test commits, PR reviews)
  // and must be counted alongside formal test artefacts.
  const experimentation = report.documentationAppendix.experimentation ?? [];
  const github = report.documentationAppendix.githubArtefacts ?? [];
  if (
    experimentation.length === 0 &&
    report.documentationAppendix.testingAndBenchmarking.length === 0 &&
    github.length === 0
  ) {
    gaps.push({ label: 'No experimentation or testing evidence', href: '/evidence' });
  }

  if (!report.documentationAppendix.projectPlansAndPrds?.length) {
    gaps.push({ label: 'No PRD, plan or technical spec', href: projectHref });
  }

  // Unreviewed time is excluded from the claim — an auditor-visible substantiation hole.
  if (pendingCount > 0) {
    gaps.push({
      label: `${pendingCount} time entr${pendingCount === 1 ? 'y' : 'ies'} awaiting review — excluded from the claim`,
      href: `${projectHref}?tab=timewriting`,
    });
  }

  return gaps;
}

function useProjectEligibility(projectId: string) {
  return useQuery<ProjectEligibilityReport>({
    queryKey: ['project-eligibility', projectId],
    queryFn: async () => {
      const res = await customInstance<{ data: ProjectEligibilityReport }>({
        url: `/api/projects/${projectId}/eligibility`,
        method: 'GET',
      });
      return res.data;
    },
    staleTime: 60_000,
  });
}

function statusConfig(status: AuditStatus) {
  if (status === 'audit_ready') return {
    label: 'Audit ready',
    cls: 'bg-positive-subtle text-positive-foreground border border-positive/20',
    Icon: CheckCircle2,
  };
  if (status === 'gaps') return {
    label: 'Gaps to close',
    cls: 'bg-warning-subtle text-warning-foreground border border-warning/20',
    Icon: AlertCircle,
  };
  return {
    label: 'Not started',
    cls: 'bg-muted text-muted-foreground border border-border',
    Icon: Clock,
  };
}

function StatusBadge({ status }: { status: AuditStatus }) {
  const { label, cls, Icon } = statusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${cls}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

interface ProjectRowProps {
  project: {
    id: string;
    name: string;
    incomeYear?: string | null;
    status: string;
    tfaLocked?: boolean | null;
    rdHours?: number | null;
    pendingCount?: number | null;
  };
  onStatusResolved?: (projectId: string, status: AuditStatus) => void;
}

function ProjectRow({ project, onStatusResolved }: ProjectRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { data: report, isLoading, error, refetch } = useProjectEligibility(project.id);
  const pendingCount = project.pendingCount ?? 0;

  const auditStatus: AuditStatus = useMemo(() => {
    if (report) return deriveStatus(report, pendingCount);
    if (project.tfaLocked) return 'gaps'; // optimistic until loaded
    return 'not_started';
  }, [report, project.tfaLocked, pendingCount]);

  const gaps = useMemo(() => report ? deriveGaps(report, pendingCount) : [], [report, pendingCount]);
  const coreCount = report?.coreActivities.length ?? 0;

  // Notify parent when status is resolved from eligibility data
  useMemo(() => {
    if (report && onStatusResolved) {
      onStatusResolved(project.id, deriveStatus(report, pendingCount));
    }
  }, [report, project.id, onStatusResolved, pendingCount]);

  const rdH = typeof project.rdHours === 'number' ? project.rdHours.toFixed(1) : null;

  return (
    <div className="border border-hairline rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-2 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-medium text-foreground truncate">{project.name}</span>
            {project.incomeYear && (
              <span className="text-[11px] text-muted-foreground">{project.incomeYear}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
            {rdH && <span>{rdH} h R&D</span>}
            {!isLoading && report && <span>{coreCount} core</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isLoading ? (
            <div className="h-5 w-24 bg-muted rounded-full animate-pulse" />
          ) : error ? (
            <button
              onClick={(e) => { e.stopPropagation(); void refetch(); }}
              className="text-[11px] text-destructive flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          ) : (
            <StatusBadge status={auditStatus} />
          )}
          {auditStatus === 'gaps' ? (
            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          ) : (
            <Link
              href={`/projects/${project.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </button>

      {expanded && auditStatus === 'gaps' && (
        <div className="border-t border-hairline bg-warning-subtle/40 px-4 py-3 space-y-3">
          {isLoading ? (
            <div className="h-4 w-48 bg-muted rounded animate-pulse" />
          ) : gaps.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No gaps found — status will update shortly.</p>
          ) : (
            <>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-warning-foreground mb-2">
                  Gaps to close · {gaps.length}
                </p>
                <ul className="space-y-1.5">
                  {gaps.map((gap, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12px] text-warning-foreground">
                      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <Link href={gap.href} className="hover:underline underline-offset-2 flex-1">
                        {gap.label}
                        <ChevronRight className="h-3 w-3 inline ml-0.5 opacity-60" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Documentation evidence counts */}
              {report && (
                <div className="pt-2 border-t border-hairline/50 space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Documentation evidence
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[12px] text-muted-foreground">
                    <span>PRDs, plans &amp; specs: {report.documentationAppendix.projectPlansAndPrds.length}</span>
                    <span>GitHub artefacts: {report.documentationAppendix.githubArtefacts.length}</span>
                    <span>Testing &amp; benchmarking: {report.documentationAppendix.testingAndBenchmarking.length}</span>
                    <span>Experimentation trails: {(report.documentationAppendix.experimentation ?? []).length}</span>
                    <span>AI model evaluation: {report.documentationAppendix.aiModelEvaluation.length}</span>
                    <span>User feedback: {report.documentationAppendix.userFeedback.length}</span>
                  </div>
                </div>
              )}

              {/* TFA problem statement snippet */}
              {report?.tfa?.locked && report.tfa.problemStatement && (
                <div className="pt-2 border-t border-hairline/50 space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Technical challenge
                  </p>
                  <p className="text-[12px] text-foreground/80 leading-relaxed">
                    {report.tfa.problemStatement.length > 120
                      ? `${report.tfa.problemStatement.slice(0, 120)}…`
                      : report.tfa.problemStatement}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'audit_ready', label: 'Ready' },
  { key: 'gaps', label: 'Gaps' },
  { key: 'not_started', label: 'Not started' },
];

export function AuditReadinessPage() {
  const { data, isLoading, error, refetch } = useGetProjects();
  const { data: claimsData } = useGetClaims();
  const { user } = useAuthStore();
  const [resolvedStatuses, setResolvedStatuses] = useState<Record<string, AuditStatus>>({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleStatusResolved = useMemo(() => (projectId: string, status: AuditStatus) => {
    setResolvedStatuses((prev) => {
      if (prev[projectId] === status) return prev;
      return { ...prev, [projectId]: status };
    });
  }, []);

  const allProjects = (data?.data?.items ?? []) as Array<{
    id: string;
    name: string;
    incomeYear?: string | null;
    status: string;
    tfaLocked?: boolean | null;
    rdHours?: number | null;
    pendingCount?: number | null;
    productGroup?: string | null;
  }>;

  const activeRdProjects = allProjects.filter((p) => p.status !== 'archived' && p.status !== 'no_rd');

  // Apply search + status filter
  const filteredProjects = useMemo(() => {
    return activeRdProjects
      .filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()))
      .filter((p) => {
        if (statusFilter === 'all') return true;
        const status = resolvedStatuses[p.id] ?? (p.tfaLocked ? 'gaps' : 'not_started');
        return status === statusFilter;
      });
  }, [activeRdProjects, search, statusFilter, resolvedStatuses]);

  // Group by productGroup (from filtered set)
  const { groups, ungrouped } = useMemo(() => {
    const byGroup: Map<string, typeof filteredProjects> = new Map();
    const ung: typeof filteredProjects = [];
    for (const p of filteredProjects) {
      const g = p.productGroup?.trim();
      if (g) {
        const arr = byGroup.get(g) ?? [];
        arr.push(p);
        byGroup.set(g, arr);
      } else {
        ung.push(p);
      }
    }
    const sorted = [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
    return { groups: sorted, ungrouped: ung };
  }, [filteredProjects]);

  // Live audit card: count resolved statuses (across all active projects, not filtered)
  const auditReadyCount = Object.values(resolvedStatuses).filter((s) => s === 'audit_ready').length;
  const totalActive = activeRdProjects.length;

  // FY for the live audit card — most common income year across active projects
  const fyLabel = useMemo(() => {
    const fyCounts: Record<string, number> = {};
    for (const p of activeRdProjects) {
      if (p.incomeYear) fyCounts[p.incomeYear] = (fyCounts[p.incomeYear] ?? 0) + 1;
    }
    const sorted = Object.entries(fyCounts).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] ?? null;
  }, [activeRdProjects]);

  // The claim (if any) for the active income year — powers the "review readiness summary" link
  const activeClaimId = useMemo(() => {
    if (!fyLabel) return null;
    const claims = claimsData?.data?.items ?? [];
    const match = claims.find((c) => c.incomeYear === fyLabel);
    return match?.id ?? null;
  }, [claimsData, fyLabel]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  if (error) {
    return <InlineError message="Failed to load audit readiness report." onRetry={() => { void refetch(); }} />;
  }

  if (activeRdProjects.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 bg-positive-subtle">
            <ShieldCheck className="h-5 w-5 text-positive" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Audit Readiness</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Portfolio-wide eligibility evidence</p>
          </div>
        </div>
        <div className="text-center py-16">
          <ShieldCheck className="h-10 w-10 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
          <p className="text-sm font-medium">No active R&D projects</p>
          <p className="text-xs text-muted-foreground mt-1">Create a project and lock its TFA to begin building the evidence portfolio.</p>
          <Link
            href="/projects/new"
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Create project
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 bg-positive-subtle">
            <ShieldCheck className="h-5 w-5 text-positive" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Audit Readiness</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Portfolio-wide eligibility evidence, assembled from what Edison already holds.
            </p>
          </div>
        </div>
        {activeClaimId ? (
          <Link
            href={`/claims/${activeClaimId}/summary`}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-hairline text-[13px] font-medium text-foreground hover:bg-surface-2 transition-colors"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Review readiness summary
          </Link>
        ) : (
          <span
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-hairline text-[13px] font-medium text-muted-foreground cursor-not-allowed"
            title="Create a claim to generate a summary"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Create a claim to generate a summary
          </span>
        )}
      </div>

      {/* Live audit card */}
      <div className="rounded-2xl border border-hairline bg-card overflow-hidden" style={{ borderLeft: '4px solid oklch(var(--color-primary))' }}>
        <div className="flex items-start gap-4 px-5 py-4">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold text-foreground">
              Live audit — {user?.name?.split(' ')[0] ?? 'your'} portfolio{fyLabel ? `, ${fyLabel}` : ''}
            </p>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {totalActive > 0
                ? `${auditReadyCount} of ${totalActive} project${totalActive !== 1 ? 's' : ''} audit-ready · the report answers each auditor question from existing TFA, competence & artefact data`
                : 'No active R&D projects in portfolio'}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-2xl font-semibold tabular-nums text-foreground">
              {totalActive > 0 ? `${Math.round((auditReadyCount / totalActive) * 100)}%` : '—'}
            </p>
            <p className="text-[11px] text-muted-foreground">portfolio ready</p>
          </div>
        </div>
      </div>

      {/* Search + status filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="w-full pl-9 pr-4 py-2 rounded-[9px] border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-[oklch(0.28_0.05_255_/_0.25)]"
          />
        </div>
        <FilterChipGroup chips={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
      </div>

      {/* Grouped project list — bounded scroll */}
      <div className="max-h-[600px] overflow-y-auto rounded-[14px] border border-border bg-card p-4">
        {filteredProjects.length === 0 ? (
          <EmptyState
            title={search ? 'No projects match your search' : 'No projects match this filter'}
            body={search ? 'Try a different search term.' : 'Try selecting a different status filter.'}
            action={
              !search && statusFilter === 'all'
                ? <Link href="/projects/new"><Button size="sm">New project</Button></Link>
                : undefined
            }
          />
        ) : (
          <div className="space-y-6">
            {groups.map(([groupName, projects]) => (
              <section key={groupName}>
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <h2 className="text-[13px] font-semibold text-foreground">{groupName}</h2>
                  <span className="text-[11px] text-muted-foreground">· {projects.length} project{projects.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {projects.map((p) => (
                    <ProjectRow key={p.id} project={p} onStatusResolved={handleStatusResolved} />
                  ))}
                </div>
              </section>
            ))}

            {ungrouped.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-[13px] font-semibold text-muted-foreground">Other products</h2>
                </div>
                {ungrouped.length > 3 && (
                  <p className="text-[11px] text-muted-foreground mb-2">
                    {ungrouped.map((p) => p.name).join(' · ')}
                  </p>
                )}
                <div className="space-y-2">
                  {ungrouped.map((p) => (
                    <ProjectRow key={p.id} project={p} onStatusResolved={handleStatusResolved} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Timesheet evidence note */}
      <div className="flex items-start gap-3 rounded-xl border border-hairline bg-surface-2 px-4 py-3">
        <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          <span className="font-medium text-foreground">Timesheet evidence</span>
          {' '}— Manual and AI-classified time entries are available from the{' '}
          <Link href="/timewriting" className="underline underline-offset-2 hover:text-foreground transition-colors">
            Timewriting
          </Link>{' '}
          page. Export includes hours per person, per activity, per project.
        </p>
      </div>

      <p className="text-[11px] text-muted-foreground text-center pb-4">
        Group projects by product using the Product group field on each project's Overview tab.
      </p>
    </div>
  );
}
